import {AbstractControl, FormArray, FormGroup, UntypedFormControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {AssistanceApplicationFileType} from '../../../shared/enums/assistance-application-file-type';
import {EmploymentStatus} from '../../../shared/enums/employment-status';
import {environment} from '../../../../environments/environment';
import {AnnualIncomeService} from '../../../services/AnnualIncomeService';

function countFileType(fileType: AssistanceApplicationFileType, filesArray: FormArray): number {
    return filesArray.controls.filter(control => control.get('fileType').value === fileType).length;
}

export function publicAssistanceApplicationRequiredDocumentsUploadedValidator(): ValidatorFn {
    return (form: FormGroup): ValidationErrors | null => {

        /* ------------------------------------------------------------------
         * helpers & raw data
         * ---------------------------------------------------------------- */
        const assistanceRequest = (form.get('standardQuestions.assistanceRequestDescription') as FormGroup).value;
        const applicantInformation = (form.get('standardQuestions.applicantInformation') as FormGroup).value;

        const applicantAdult = {
            firstName: applicantInformation.firstName,
            lastName: applicantInformation.lastName,
            totalMonthlyIncome: +applicantInformation.totalMonthlyIncome,
            employmentStatus: applicantInformation.employmentStatus
        };

        const additionalAdults = (form.get('standardQuestions.householdInformation.adults') as FormArray).value as {
            employmentStatus: EmploymentStatus;
        }[];

        const filesArray = form.get('files') as FormArray;
        const allAdults   = [applicantAdult, ...additionalAdults];

        const count = (t: AssistanceApplicationFileType) => countFileType(t, filesArray);

        /* ------------------------------------------------------------------
         * core / always‑required docs
         * ---------------------------------------------------------------- */
        const errors: ValidationErrors = {};

        if (count(AssistanceApplicationFileType.PhotoId) < 1) {
            errors.photoIdRequired = true;
        }

        /* employment / residency equivalency --------------------------------*/
        const employmentEquivalents = [
            AssistanceApplicationFileType.UtilityBill,
            AssistanceApplicationFileType.TaxStatement,
            AssistanceApplicationFileType.TaxReturns,
            AssistanceApplicationFileType.BankStatement,
            AssistanceApplicationFileType.GovtOrCourtRecord,
            AssistanceApplicationFileType.GovtBenefitsStatement,
            AssistanceApplicationFileType.SsiSsdiAwardLetter,
            AssistanceApplicationFileType.UnemploymentAward,
            AssistanceApplicationFileType.WicSnapAwardLetter,
            AssistanceApplicationFileType.EmployerLetter
        ];

        const employmentDocs = employmentEquivalents.reduce((s, t) => s + count(t), 0);
        if (employmentDocs < 1) errors.utilityOrEquivalentRequired = true;

        /* residency (STLWater only) ----------------------------------------*/
        const residencyEquivalents = [
            AssistanceApplicationFileType.ProofOfResidence,
            AssistanceApplicationFileType.PhotoId,
            AssistanceApplicationFileType.UtilityBill,
            AssistanceApplicationFileType.TaxReturns,
            AssistanceApplicationFileType.VoterRegistration
        ];
        const residencyDocs = residencyEquivalents.reduce((s, t) => s + count(t), 0);
        if (assistanceRequest.stlWaterWater && residencyDocs < 1) {
            errors.residenceProofRequired = true;
        }

        /* income docs ------------------------------------------------------*/
        const incomeDocuments = {
            checkStub:              count(AssistanceApplicationFileType.CheckStub),
            pensionLetter:          count(AssistanceApplicationFileType.PensionLetter),
            govtBenefitsStatement:  count(AssistanceApplicationFileType.GovtBenefitsStatement),
            ssiSsdiAwardLetter:     count(AssistanceApplicationFileType.SsiSsdiAwardLetter),
            unemploymentAward:      count(AssistanceApplicationFileType.UnemploymentAward),
            wicSnapAwardLetter:     count(AssistanceApplicationFileType.WicSnapAwardLetter),
            taxReturns:             count(AssistanceApplicationFileType.TaxReturns),
            profitLossStatement:    count(AssistanceApplicationFileType.ProfitLossStatement),
            armedForcesAllotment:   count(AssistanceApplicationFileType.ArmedForcesAllotment),
            tanf:                   count(AssistanceApplicationFileType.TANF),
            childSupport:           count(AssistanceApplicationFileType.ChildSupport),
            unionFunds:             count(AssistanceApplicationFileType.UnionFunds)
        };

        const selfEmployedAdults      = allAdults.filter(a => a.employmentStatus === EmploymentStatus.SelfEmployed);
        const nonSelfEmployedAdults   = allAdults.filter(a => a.employmentStatus !== EmploymentStatus.SelfEmployed);

        if (selfEmployedAdults.length &&
            (incomeDocuments.taxReturns + incomeDocuments.profitLossStatement) < selfEmployedAdults.length &&
            form.dirty) {
            errors.incomeDocumentRequiredForSelfEmployed = true;
        }

        const nonSEIncomeCount =
            incomeDocuments.checkStub           + incomeDocuments.pensionLetter +
            incomeDocuments.govtBenefitsStatement + incomeDocuments.ssiSsdiAwardLetter +
            incomeDocuments.unemploymentAward     + incomeDocuments.wicSnapAwardLetter +
            incomeDocuments.armedForcesAllotment  + incomeDocuments.tanf +
            incomeDocuments.childSupport          + incomeDocuments.unionFunds;

        if (nonSelfEmployedAdults.length && nonSEIncomeCount < nonSelfEmployedAdults.length && form.dirty) {
            errors.incomeDocumentRequiredForNonSelfEmployed = true;
        }

        /* area‑specific requirements ---------------------------------------*/

        /* --- utility assistance (UHONS / UFF) --- */
        if (assistanceRequest.uhonsElectric ||
            assistanceRequest.uhonsGasHeat ||
            assistanceRequest.uhonsWater ||
            assistanceRequest.uhonsSewer ||
            assistanceRequest.uffUtilityBills) {
            if (count(AssistanceApplicationFileType.UtilityBill) +
                count(AssistanceApplicationFileType.UtilityShutoffNotice) < 1) {
                errors.utilityBillOrShutoffNoticeRequired = true;
            }
        }

        /* --- childcare --- */
        if (assistanceRequest.uhonsChildcareExpenses ||
            assistanceRequest.uffStateLicensedChildcareProvider) {
            if (count(AssistanceApplicationFileType.ChildcareBill) < 1) {
                errors.childcareBillRequired = true;
            }
        }

        /* --- car repair --- */
        if (assistanceRequest.uhonsCarRepair || assistanceRequest.uffCarRepair) {
            if (count(AssistanceApplicationFileType.AutoRepairBill) < 1) {
                errors.autoRepairBillRequired = true;
            }
        }

        /* --- rent assistance (UHONS) --- */
        if (assistanceRequest.uhonsRent) {
            if (count(AssistanceApplicationFileType.LeaseAgreement) < 1) {
                errors.leaseAgreementRequired = true;
            }
            if (count(AssistanceApplicationFileType.DelinquentRentNotice) < 1) {
                errors.delinquentRentNoticeRequired = true;
            }
        }

        /* --- rent assistance (UFF) --- */
        if (assistanceRequest.uffRentPayment) {
            if (count(AssistanceApplicationFileType.LeaseAgreement) < 1) {
                errors.leaseAgreementRequired = true;
            }
            if (count(AssistanceApplicationFileType.DelinquentRentNotice) < 1) {
                errors.delinquentRentNoticeRequired = true;
            }
            if (count(AssistanceApplicationFileType.LandlordProofOfOwnership) < 1) {
                errors.landlordOwnershipRequired = true;
            }
            if (count(AssistanceApplicationFileType.RentLandlordForm) < 1) {
                errors.rentLandlordFormRequired = true;
            }
        }

        /* --- security deposit (UFF) --- */
        if (assistanceRequest.uffSecurityDeposit) {
            if (count(AssistanceApplicationFileType.LeaseAgreement) < 1) {
                errors.leaseAgreementRequired = true;
            }
            if (count(AssistanceApplicationFileType.LandlordProofOfOwnership) < 1) {
                errors.landlordOwnershipRequired = true;
            }
        }

        /* --- eviction --- */
        if (assistanceRequest.evictionNotification) {
            if (count(AssistanceApplicationFileType.EvictionCourtOrder) +
                count(AssistanceApplicationFileType.EvictionNotice) < 1) {
                errors.evictionCourtRecordRequired = true;
            }
        }

        /* --- mortgage (UFF) --- */
        if (assistanceRequest.uffMortgagePayment &&
            count(AssistanceApplicationFileType.MortgageStatement) < 1) {
            errors.mortgageStatementRequired = true;
        }

        /* --- moving expenses (UFF) --- */
        if (assistanceRequest.uffMovingExpenses &&
            count(AssistanceApplicationFileType.MovingExpenseBill) < 1) {
            errors.movingExpenseBillRequired = true;
        }

        /* --- car insurance / payment (UFF) --- */
        if (assistanceRequest.uffCarInsurance &&
            count(AssistanceApplicationFileType.AutoInsuranceBill) < 1) {
            errors.autoInsuranceBillRequired = true;
        }

        if (assistanceRequest.uffCarPayment &&
            count(AssistanceApplicationFileType.AutoPaymentStatement) < 1) {
            errors.autoPaymentStatementRequired = true;
        }

        /* auto‑title required for any car‑related UFF */
        if ((assistanceRequest.uffCarInsurance ||
                assistanceRequest.uffCarPayment ||
                assistanceRequest.uffCarRepair) &&
            count(AssistanceApplicationFileType.AutoTitle) < 1) {
            errors.autoTitleRequired = true;
        }

        /* --- tuition (UFF) --- */
        if (assistanceRequest.uffTuition &&
            count(AssistanceApplicationFileType.TuitionBill) < 1) {
            errors.tuitionBillRequired = true;
        }

        /* --- school supplies (UFF) --- */
        if (assistanceRequest.uffChildEdSupportUniforms) {
            if (count(AssistanceApplicationFileType.ChildEducationBill) < 1) {
                errors.childEducationBillRequired = true;
            }
            if (count(AssistanceApplicationFileType.ChildEnrollmentProof) < 1) {
                errors.childEnrollmentProofRequired = true;
            }
            if (count(AssistanceApplicationFileType.ChildEducationProgressReport) < 1) {
                errors.childProgressReportRequired = true;
            }
        }

        /* --- tutoring (UFF) --- */
        if (assistanceRequest.uffChildSupportTutoring) {
            if (count(AssistanceApplicationFileType.ChildTutoringBill) < 1) {
                errors.tutoringInvoiceRequired = true;
            }
            if (count(AssistanceApplicationFileType.ChildEnrollmentProof) < 1) {
                errors.childEnrollmentProofRequired = true;
            }
        }

        /* --- STL Water assistance --- */
        if (assistanceRequest.stlWaterWater) {
            if (count(AssistanceApplicationFileType.DelinquentWaterBill) < 1) {
                errors.delinquentWaterBillRequired = true;
            }
        }

        /* --- SSN proof (universal) --- */
        const ssnDocs =
            count(AssistanceApplicationFileType.SsnProof)           +
            count(AssistanceApplicationFileType.TaxReturns)         +
            count(AssistanceApplicationFileType.SsiSsdiAwardLetter);
        if (ssnDocs < 1) errors.ssnProofRequired = true;

        /* ------------------------------------------------------------------ */
        return Object.keys(errors).length ? errors : null;
    };
}

export function zipCodeValidator(): ValidatorFn {
    return (control: UntypedFormControl) => {
        if (!control.value) {
            return null;
        }
        const value = control.value + '';
        if (value.length !== 5) {
            return {zipCode: 'Please enter a valid zip code using a five digit format (0-9).'};
        }
        return null;
    };
}

export function ssnValidator(): ValidatorFn {
    return (control: UntypedFormControl) => {
        if (!control.value) {
            return null;
        }
        const value = (control.value + '').replace(/-/g, '');
        if (value.length !== 9) {
            return {ssn: 'Please enter a valid social security number using a nine digit format (0-9).'};
        }
        return null;
    };
}

export function stateValidator(): ValidatorFn {
    const invalidStates = ['IL', 'Other'];

    return (control: UntypedFormControl) => {
        if (!control.value) {
            return null;
        }
        if (invalidStates.includes(control.value)) {
            return {
                invalidState: 'To be eligible for this program you must currently live in Missouri. Based on your\n' +
                    '                answer, you are not qualified for this program. To see what other programs and services\n' +
                    '                might be available in your community, please dial 211.'
            };
        }
        return null;
    };
}

export function stateValidatorForValidStates(): ValidatorFn {
    const validStates = ['MO'];

    return (control: UntypedFormControl) => {
        if (!control.value) {
            return null;
        }
        if (!validStates.includes(control.value)) {
            return {
                invalidState: 'To be eligible for this program you must currently live in Missouri. Based on your\n' +
                    '                answer, you are not qualified for this program. To see what other programs and services\n' +
                    '                might be available in your community, please dial 211.'
            };
        }
        return null;
    };
}

export function stlWaterAccountNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        // Return null if control value is empty (to allow required validator to handle empty input)
        if (!control.value) {
            return null;
        }

        // Regex: 4-7 digits, hyphen, 4-6 digits
        const regex = /^\d{4,7}-\d{4,6}$/;

        // Test the value against the regex
        return regex.test(control.value) ? null : { companyAccountNumber: 'Invalid account number' };
    };
}
